import React, { useEffect, useRef, useState } from "react";
import useSWR from "swr";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { UploadCloud, CheckCircle2, XCircle, Clock, FileSpreadsheet, Trash2, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { useAuth } from "@/context/AuthContext";

const fetcher = (url) => api.get(url).then((r) => r.data);

const STEPS_ORDER = [
  "queued", "downloading", "opening workbook",
  "Parsing Branch Master",
  "Parsing Daily Sales Report",
  "Parsing Gift Voucher Report",
  "Parsing Membership Card Balance",
  "Parsing Operator Performance",
  "Parsing Punch In-Out Report",
  "Parsing Therapist Payout",
  "Parsing Target Budget",
  "Parsing Cost Others",
  "Parsing Employee Master",
  "Parsing Appointment Report",
  "Parsing Customer Master",
  "Aggregating monthly facts",
  "done",
];

function stepProgress(step) {
  const i = STEPS_ORDER.indexOf(step);
  if (i < 0) return 5;
  return Math.round(((i + 1) / STEPS_ORDER.length) * 100);
}

export default function Upload() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const inputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [jobId, setJobId] = useState(null);
  const [dragging, setDragging] = useState(false);
  const [inspectJobId, setInspectJobId] = useState(null);

  const { data: job, mutate: refetchJob } = useSWR(
    jobId ? `/api/uploads/jobs/${jobId}` : null,
    fetcher,
    { refreshInterval: (data) => (data && ["completed", "failed"].includes(data.status) ? 0 : 1200) }
  );
  const { data: history, mutate: refetchHistory } = useSWR("/api/uploads/history", fetcher, { refreshInterval: 4000 });

  useEffect(() => {
    if (job && ["completed", "failed"].includes(job.status)) {
      refetchHistory();
    }
  }, [job, refetchHistory]);

  const upload = async () => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/api/uploads", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setJobId(data.job_id);
      toast.success("Upload received. Parsing started.");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  const handlePick = (f) => {
    if (!f) return;
    if (!f.name.toLowerCase().endsWith(".xlsx")) {
      toast.error("Please choose an .xlsx workbook");
      return;
    }
    setFile(f);
  };

  const parsing = job && !["completed", "failed"].includes(job.status);
  const done = job?.status === "completed";
  const failed = job?.status === "failed";

  return (
    <div className="p-6 md:p-10 max-w-5xl">
      <div className="text-[10px] font-bold uppercase tracking-[0.25em] text-stone-500">Data ingestion</div>
      <h1 className="font-heading text-3xl sm:text-4xl font-light tracking-tighter text-stone-900 mt-1">
        Upload monthly MIS pack
      </h1>
      <p className="mt-3 text-sm text-stone-600 max-w-xl">
        Drop the 13-sheet Excel workbook. Parsing runs in the background — feel free to
        navigate away; results append, they never overwrite prior months.
      </p>

      {/* Drop zone */}
      <div
        onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={(e) => {
          e.preventDefault(); setDragging(false);
          const f = e.dataTransfer.files?.[0];
          handlePick(f);
        }}
        onClick={() => inputRef.current?.click()}
        data-testid="upload-dropzone"
        className={cn(
          "mt-8 rounded-lg border-2 border-dashed p-10 text-center cursor-pointer transition-colors",
          dragging ? "border-brand bg-brand/5" : "border-stone-300 bg-white hover:border-stone-400"
        )}
      >
        <input
          type="file"
          ref={inputRef}
          accept=".xlsx"
          className="hidden"
          onChange={(e) => handlePick(e.target.files?.[0])}
          data-testid="upload-file-input"
        />
        <UploadCloud className="w-8 h-8 mx-auto text-stone-500" />
        <div className="mt-4 font-heading text-lg text-stone-900">
          {file ? file.name : "Drop your MIS Input File Pack (.xlsx)"}
        </div>
        <div className="text-xs font-mono text-stone-500 mt-1">
          {file ? `${(file.size / (1024 * 1024)).toFixed(2)} MB` : "or click to browse"}
        </div>
      </div>

      {file && !parsing && (
        <div className="mt-4 flex gap-3">
          <Button
            onClick={upload}
            className="bg-brand hover:bg-brand-hover text-white"
            data-testid="upload-submit-btn"
          >
            Start parsing
          </Button>
          <Button variant="outline" onClick={() => { setFile(null); setJobId(null); }} data-testid="upload-clear-btn">
            Clear
          </Button>
        </div>
      )}

      {/* Job status */}
      {job && (
        <Card className="mt-8 p-6 bg-white border-stone-200" data-testid="job-card">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <FileSpreadsheet className="w-5 h-5 text-brand" />
              <div>
                <div className="font-medium text-stone-900">{job.filename}</div>
                <div className="text-xs font-mono text-stone-500">job {job.job_id.slice(0, 8)}</div>
              </div>
            </div>
            <Badge
              className={cn(
                "font-mono",
                done && "bg-brand-good text-white",
                failed && "bg-brand-bad text-white",
                parsing && "bg-stone-200 text-stone-800"
              )}
              data-testid="job-status-badge"
            >
              {job.status}
            </Badge>
          </div>
          <div className="mt-5">
            <div className="flex items-center justify-between text-xs text-stone-600 mb-2">
              <span className="font-mono">{job.current_step}</span>
              <span className="font-mono">{stepProgress(job.current_step)}%</span>
            </div>
            <Progress value={stepProgress(job.current_step)} className="h-2" />
          </div>

          {done && job.stats && (
            <div className="mt-5 grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="job-summary">
              <Stat label="Monthly facts" value={job.stats.monthly_facts_upserts} />
              <Stat label="Branches" value={job.stats.branches_touched?.length || 0} />
              <Stat label="Months" value={job.stats.months_touched?.length || 0} />
              <Stat label="DSR rows" value={job.stats.sheets?.["Daily Sales Report"] || 0} />
            </div>
          )}
          {failed && (
            <div className="mt-5 flex items-start gap-2 rounded-md bg-red-50 border border-red-200 p-3 text-sm text-red-800" data-testid="job-error">
              <XCircle className="w-4 h-4 mt-0.5 shrink-0" />
              <div>
                <div className="font-medium">Parsing failed</div>
                <div className="mt-1 font-mono text-xs">{job.error}</div>
              </div>
            </div>
          )}
          {done && (
            <div className="mt-5">
              <a href="/dashboard" className="text-sm text-brand hover:underline font-medium" data-testid="job-view-dashboard">
                View dashboard →
              </a>
            </div>
          )}
        </Card>
      )}

      {/* History */}
      <div className="mt-12">
        <div className="text-[10px] font-bold uppercase tracking-[0.25em] text-stone-500 mb-3">
          Upload history
        </div>
        <div className="rounded-lg border border-stone-200 bg-white overflow-hidden">
          <table className="w-full text-sm" data-testid="upload-history-table">
            <thead className="bg-stone-50 border-b border-stone-200">
              <tr className="text-left text-[10px] font-bold uppercase tracking-[0.15em] text-stone-500">
                <th className="px-4 py-3">Filename</th>
                <th className="px-4 py-3">By</th>
                <th className="px-4 py-3">Uploaded</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Rows</th>
                {isAdmin && <th className="px-4 py-3 text-right">Actions</th>}
              </tr>
            </thead>
            <tbody>
              {(history || []).map((h) => (
                <tr key={h.job_id} className="border-b border-stone-100 last:border-0">
                  <td className="px-4 py-3 font-mono text-xs">{h.filename}</td>
                  <td className="px-4 py-3">{h.uploaded_by}</td>
                  <td className="px-4 py-3 font-mono text-xs text-stone-500">
                    {h.uploaded_at ? new Date(h.uploaded_at).toLocaleString() : "—"}
                  </td>
                  <td className="px-4 py-3">
                    <span className={cn(
                      "inline-flex items-center gap-1 text-xs font-mono",
                      h.status === "completed" && "text-brand-good",
                      h.status === "failed" && "text-brand-bad",
                      !["completed", "failed"].includes(h.status) && "text-stone-500",
                    )}>
                      {h.status === "completed" && <CheckCircle2 className="w-3.5 h-3.5" />}
                      {h.status === "failed" && <XCircle className="w-3.5 h-3.5" />}
                      {!["completed", "failed"].includes(h.status) && <Clock className="w-3.5 h-3.5" />}
                      {h.status}
                    </span>
                  </td>
                  <td className="px-4 py-3 font-mono text-xs">
                    {h.stats?.monthly_facts_upserts ?? "—"}
                  </td>
                  {isAdmin && (
                    <td className="px-4 py-3 text-right">
                      {h.status === "completed" && (
                        <button
                          onClick={() => setInspectJobId(h.job_id)}
                          className="inline-flex items-center gap-1 text-xs font-medium text-brand hover:underline"
                          data-testid={`inspect-btn-${h.job_id}`}
                        >
                          Inspect <ChevronRight className="w-3 h-3" />
                        </button>
                      )}
                    </td>
                  )}
                </tr>
              ))}
              {(!history || history.length === 0) && (
                <tr>
                  <td colSpan={isAdmin ? 6 : 5} className="px-4 py-8 text-center text-stone-500 text-sm">
                    No uploads yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {isAdmin && (
        <InspectDialog
          jobId={inspectJobId}
          onClose={() => setInspectJobId(null)}
          onChanged={() => refetchHistory()}
        />
      )}
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="rounded-md border border-stone-200 bg-stone-50 p-3">
      <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-stone-500">{label}</div>
      <div className="font-mono text-lg font-medium mt-1">{value}</div>
    </div>
  );
}

function InspectDialog({ jobId, onClose, onChanged }) {
  const { data, mutate, isLoading } = useSWR(
    jobId ? `/api/uploads/jobs/${jobId}/inspect` : null,
    (u) => api.get(u).then((r) => r.data),
  );
  const [confirm, setConfirm] = useState(null); // {kind:'sheet'|'job', target}
  const [busy, setBusy] = useState(false);

  const doDelete = async () => {
    if (!confirm) return;
    setBusy(true);
    try {
      if (confirm.kind === "sheet") {
        const enc = encodeURIComponent(confirm.target);
        const { data } = await api.delete(`/api/uploads/sheets/${enc}`);
        toast.success(`Purged ${data.extended_col_facts_deleted} column facts + zeroed ${data.base_fields_zeroed?.length || 0} fields`);
        mutate();
        onChanged?.();
      } else {
        const { data } = await api.delete(`/api/uploads/jobs/${confirm.target}`);
        const total = Object.values(data.row_counts_deleted || {}).reduce((a, b) => a + b, 0);
        toast.success(`Upload deleted — ${total} rows removed${data.gridfs_deleted ? " + workbook file" : ""}. Remaining uploads are being re-processed; figures will be complete in a few minutes.`);
        onChanged?.();
        onClose();
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
      setConfirm(null);
    }
  };

  const job = data?.job;
  const sheets = data?.sheets || [];

  return (
    <>
      <Dialog open={!!jobId} onOpenChange={(o) => { if (!o) onClose(); }}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto" data-testid="inspect-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl font-light tracking-tight">
              {job?.filename || "Upload inspection"}
            </DialogTitle>
            <DialogDescription className="text-xs font-mono text-stone-500">
              {job ? `job ${job.job_id.slice(0, 12)} · uploaded by ${job.uploaded_by} · ${new Date(job.uploaded_at).toLocaleString()}` : ""}
            </DialogDescription>
          </DialogHeader>

          {isLoading && <div className="py-10 text-center text-sm text-stone-500">Loading…</div>}

          {!isLoading && sheets.length === 0 && (
            <div className="py-8 text-center text-sm text-stone-500">
              No sheets recorded for this upload.
            </div>
          )}

          {!isLoading && sheets.length > 0 && (
            <div className="space-y-3">
              {sheets.map((s) => (
                <div
                  key={s.name}
                  className="rounded-md border border-stone-200 bg-white p-4"
                  data-testid={`sheet-row-${s.name}`}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="min-w-0 flex-1">
                      <div className="font-heading text-lg text-stone-900">{s.name}</div>
                      <div className="mt-2 grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs font-mono">
                        <MiniStat label="Rows read" value={s.rows_read.toLocaleString()} />
                        <MiniStat label="Col facts" value={s.extended_col_facts_count.toLocaleString()} />
                        <MiniStat label="Months" value={s.months_covered.length} />
                        <MiniStat label="Branches" value={s.branches_touched} />
                      </div>
                      {s.months_covered.length > 0 && (
                        <div className="mt-2 flex flex-wrap gap-1">
                          {s.months_covered.map((m) => (
                            <span key={m} className="px-1.5 py-0.5 rounded bg-stone-100 text-[10px] font-mono text-stone-600">
                              {m}
                            </span>
                          ))}
                        </div>
                      )}
                      {s.base_fields_derived.length > 0 && (
                        <div className="mt-3">
                          <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-stone-500 mb-1">
                            Base fields fed by this sheet
                          </div>
                          <div className="flex flex-wrap gap-1">
                            {s.base_fields_derived.map((f) => (
                              <span key={f} className="px-1.5 py-0.5 rounded bg-brand/10 text-[10px] font-mono text-brand">
                                {f}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                      {s.side_collections.length > 0 && (
                        <div className="mt-2 text-[10px] font-mono text-stone-500">
                          also wipes: {s.side_collections.join(", ")}
                        </div>
                      )}
                      {s.errors.length > 0 && (
                        <div className="mt-2 text-[11px] font-mono text-brand-bad">
                          {s.errors.map((e, i) => (<div key={i}>· {e}</div>))}
                        </div>
                      )}
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setConfirm({ kind: "sheet", target: s.name })}
                      className="text-brand-bad border-red-200 hover:bg-red-50"
                      data-testid={`delete-sheet-btn-${s.name}`}
                    >
                      <Trash2 className="w-3.5 h-3.5 mr-1.5" /> Delete
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}

          <DialogFooter className="border-t border-stone-200 pt-4 mt-4 sm:justify-between">
            <div className="text-xs text-stone-500">
              Sheet delete acts across all uploads. Deleting the upload only removes rows carrying this job&apos;s id.
            </div>
            <Button
              variant="destructive"
              onClick={() => setConfirm({ kind: "job", target: jobId })}
              disabled={!job}
              data-testid="delete-upload-btn"
            >
              <Trash2 className="w-3.5 h-3.5 mr-1.5" /> Delete entire upload
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!confirm} onOpenChange={(o) => { if (!o) setConfirm(null); }}>
        <DialogContent className="max-w-md" data-testid="confirm-dialog">
          <DialogHeader>
            <DialogTitle>
              {confirm?.kind === "sheet" ? `Delete ${confirm.target}?` : "Delete this upload?"}
            </DialogTitle>
            <DialogDescription>
              {confirm?.kind === "sheet"
                ? "This purges the sheet's discovered columns across every upload and zeroes the base fields it feeds. Cannot be undone."
                : "This removes every row derived from this specific upload plus the raw workbook file. Cannot be undone."}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirm(null)} disabled={busy}>Cancel</Button>
            <Button variant="destructive" onClick={doDelete} disabled={busy} data-testid="confirm-delete-btn">
              {busy ? "Deleting…" : "Delete"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function MiniStat({ label, value }) {
  return (
    <div>
      <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-stone-500">{label}</div>
      <div className="font-mono text-sm text-stone-900 mt-0.5">{value}</div>
    </div>
  );
}
